/**
 * Insights API error types + problem+json message extraction. Lives apart from
 * insightsClient.ts so jest/Playwright can import it without pulling
 * `import.meta`/firebase (the client re-exports it for consumers).
 */

/**
 * Structured insights API failure. `message` keeps the exact legacy
 * computation (error.message → detail → title → `HTTP <status>`), so existing
 * `instanceof Error` catch sites and message-matching heuristics keep working;
 * `code`/`title`/`detail` carry the problem+json fields the old path discarded
 * (consumed by api/errorCopy.ts).
 */
export class InsightsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly title?: string,
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'InsightsApiError';
  }
}

export interface InsightsProblemPayload {
  error?: { message?: string };
  code?: string;
  detail?: string;
  title?: string;
}

/** Builds the thrown error from a non-ok response payload (legacy message priority). */
export function insightsErrorFromProblem(
  payload: InsightsProblemPayload | undefined,
  status: number,
): InsightsApiError {
  let message: string | undefined;
  if (payload && 'error' in payload) {
    message = payload.error?.message;
  } else if (payload && 'detail' in payload) {
    message = payload.detail;
  } else if (payload && 'title' in payload) {
    message = payload.title;
  }
  return new InsightsApiError(
    message || `HTTP ${status}`,
    status,
    typeof payload?.code === 'string' ? payload.code : undefined,
    payload?.title,
    payload?.detail,
  );
}
